<template>
  <div class="px-3 pt-2 h-full overflow-auto pb-5">
    <FormSelect :label="$t('evaluate.method')" v-model="api.method" :options="methods" default-reducer class="w-full" @update:modelValue="emitPayload" />
    <PhraseEditor :label="$t('evaluate.url')" v-model="api.url" :items="variables" default-reducer class="mt-3 w-full" @update:modelValue="emitPayload" />
    <template v-if="!isAgent">
      <div class="mt-3">
        <div class="flex justify-between mb-1">
          <FormLabel :label="$t('evaluate.headers')" />
          <IconButton icon="plus" size="4" dense @click="addHeader" />
        </div>
        <div v-for="(header, index) in headers" :key="index" class="flex gap-x-2 mb-1 items-center">
          <FormInput v-model="header.key" class="w-full" placeholder="Key" @update:modelValue="emitPayload" /> :
          <PhraseEditor v-model="header.value" :items="variables" class="w-full" placeholder="Value" @update:modelValue="emitPayload" />
          <IconButton :disabled="Object.keys(headers)?.length === 1 && index === 0" icon="delete" class="text-primary" dense size="4" @click="onDeleteHeaderClick(index)" />
        </div>
      </div>
      <!-- <div class="mt-3">
      <div class="flex justify-between mb-1">
        <FormLabel :label="$t('evaluate.parameters')" />
        <IconButton icon="plus" size="4" dense @click="addParameter" />
      </div>
      <div v-for="(parameter, index) in parameters" :key="index" class="flex gap-x-2 mb-1 items-center">
        <FormInput v-model="parameter.key" class="w-full" placeholder="Key" @update:modelValue="emitPayload" /> :
        <PhraseEditor v-model="parameter.value" :items="variables" class="w-full" placeholder="Value" @update:modelValue="emitPayload" />
        <IconButton :disabled="Object.keys(parameters)?.length === 1 && index === 0" icon="delete" class="text-primary" dense size="4" @click="onDeleteParameterClick(index)" />
      </div>
    </div> -->
      <div v-if="api.method !== 'GET' && api.method !== 'DELETE'" class="mt-3">
        <div class="flex justify-between mb-1 min-h-6">
          <FormLabel :label="$t('evaluate.body')" />
          <IconButton v-if="api.body_type !== 'raw'" icon="plus" size="4" dense @click="addBodyItem" />
        </div>
        <div class="flex gap-x-5 mb-1 text-13 items-center h-6">
          <RadioButton v-model="api.body_type" name="bodyType" value="form_data" label="Form Data" @update:modelValue="onBodyTypeChange" />
          <RadioButton v-if="false" v-model="api.body_type" name="bodyType" value="url_encoded" label="URL Encoded" @update:modelValue="onBodyTypeChange" />
          <RadioButton v-model="api.body_type" name="bodyType" value="binary" label="Binary (File)" @update:modelValue="onBodyTypeChange" />
          <RadioButton v-model="api.body_type" name="bodyType" value="raw" label="Raw (Text, JSON etc.)" @update:modelValue="onBodyTypeChange" />
          <div v-if="api.body_type === 'raw'" class="ml-auto flex items-center justify-end py-1">
            <FormLabel label="Type:" class="mr-1 !mb-0" />
            <ButtonDropdown v-model="bodyLanguage" :list="bodyLanguages" class="ml-1" />
          </div>
        </div>
        <template v-if="api.body_type === 'form_data'">
          <div v-for="(parameter, index) in body.formData" :key="index" class="flex gap-x-2 mb-1 items-center">
            <FormInput v-model="parameter.key" class="w-full" placeholder="Key" @update:modelValue="emitPayload" /> :
            <FormSelect
              v-model="parameter.type"
              :options="[
                { value: 'Variable', label: 'Variable' },
                { value: 'Text', label: 'Text' },
              ]"
              class="w-150 shrink-0"
              :searchable="false"
              appendToBody
              default-reducer
            />
            <PhraseEditor v-if="parameter.type === 'Text'" v-model="parameter.value" :items="variables" class="w-full" placeholder="Value" @update:modelValue="emitPayload" />
            <VariableSelect v-if="parameter.type === 'Variable'" v-model="parameter.file" class="w-full" :excludeDataTypes="['Schedule']" @update:modelValue="emitPayload" />
            <IconButton :disabled="Object.keys(body.formData)?.length === 1 && index === 0" icon="delete" class="text-primary" dense size="4" @click="onDeleteBodyClick(index)" />
          </div>
        </template>
        <template v-if="api.body_type === 'url_encoded'">
          <div v-for="(parameter, index) in body.urlencoded" :key="index" class="flex gap-x-2 mb-1 items-center">
            <FormInput v-model="parameter.key" class="w-full" placeholder="Key" @update:modelValue="emitPayload" /> :
            <PhraseEditor v-model="parameter.value" :items="variables" class="w-full" placeholder="Value" @update:modelValue="emitPayload" />
            <IconButton :disabled="Object.keys(body.urlencoded)?.length === 1 && index === 0" icon="delete" class="text-primary" dense size="4" @click="onDeleteBodyClick(index)" />
          </div>
        </template>
        <template v-if="api.body_type === 'binary'">
          <div class="flex gap-x-2 mb-1 items-center">
            <VariableSelect label="Select file variable for upload" v-model="api.file" class="mt-3 w-full" :filter-data-type="['PDF', 'Image']" @update:modelValue="emitPayload" />
          </div>
        </template>
        <template v-if="api.body_type === 'raw'">
          <MonacoEditor
            width="100%"
            height="298"
            class="h-[200px] border border-stroke"
            v-model:value="body.raw"
            :language="bodyLanguage"
            :options="editorOptions"
            @update:value="emitPayload"
          ></MonacoEditor>
        </template>
      </div>
      <VariableSelect :label="$t('evaluate.on_error')" v-model="api.on_error" class="mt-3 w-full" :filter-data-type="['YesNo']" @update:modelValue="emitPayload" />
      <div class="mt-3 pb-7">
        <div class="flex justify-between mb-1">
          <FormLabel label="Assign Response to Variable(s)" />
          <IconButton icon="plus" size="4" dense @click="addResponseParameter" />
        </div>
        <div v-for="(parameter, index) in responseParameters" :key="index" class="flex gap-x-2 mb-1 items-center">
          <FormInput v-model="parameter.key" class="w-full" placeholder="Enter object path (@ for root or use dot notation)" @update:modelValue="emitPayload" />
          <Icon name="east" class="w-4 h-4 flex-shrink-0 text-gray-600" />
          <VariableSelect :system="false" :filterDataType="['DataTable', 'Text', 'YesNo', 'Number']" v-model="parameter.value" class="w-full" @update:modelValue="emitPayload" />
          <IconButton :disabled="Object.keys(responseParameters)?.length === 1 && index === 0" icon="delete" class="text-primary" dense size="4" @click="onDeleteResponseClick(index)" />
        </div>
      </div>
    </template>
    <div v-if="isAgent" class="text-11 text-gray-400 mt-3">The agent fills the request body and reads the response automatically. Fix the method and URL here, or leave them for the agent to set.</div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import MonacoEditor from '@guolao/vue-monaco-editor';
import { MONACO_EDITOR_DEFAULTS } from '@/constants';
import debounce from '../../../helpers/debounce';
import EvaluateItemsConfig from './EvaluateItemsConfig';

export default {
  name: 'APIConfig',
  components: { MonacoEditor },
  props: {
    item: {
      type: Object,
      default: null,
    },
    isAgent: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      api: structuredClone(EvaluateItemsConfig['action-execute-api'].payload),
      methods: [
        { label: 'GET', value: 'GET' },
        { label: 'POST', value: 'POST' },
        { label: 'PUT', value: 'PUT' },
        { label: 'PATCH', value: 'PATCH' },
        { label: 'DELETE', value: 'DELETE' },
      ],
      headers: [],
      // parameters: [],
      body: {
        formData: [],
        urlencoded: [],
        raw: '',
        binary: '',
      },
      bodyLanguage: 'json',
      bodyLanguages: [
        { label: 'Text', value: 'text' },
        // { label: 'JavaScript', value: 'javascript' },
        { label: 'JSON', value: 'json' },
        { label: 'HTML', value: 'html' },
        { label: 'XML', value: 'xml' },
      ],
      responseParameters: [],
      editorOptions: MONACO_EDITOR_DEFAULTS,
    };
  },
  computed: {
    ...mapState(['availableDataAdapters']),
    ...mapGetters(['getProjectDataAdapters', 'getProjectVariables']),
    variables() {
      return Object.keys(this.getProjectVariables()).map((i) => ({ label: i, value: i }));
    },
    dataAdapters() {
      return Object.keys(this.getProjectDataAdapters()).map((key) => {
        return {
          label: this.availableDataAdapters[key].name,
          value: key,
        };
      });
    },
    projectId() {
      return this.$route.params.projectId;
    },
    payload() {
      const api = structuredClone(this.api);
      api.headers = this.convertArrayToObject(this.headers);
      // api.parameters = this.convertArrayToObject(this.parameters);
      api.response = this.convertArrayToObject(this.responseParameters);
      if (!['GET', 'DELETE'].includes(api.method)) {
        if (api.body_type === 'form_data') {
          if (!this?.body?.formData) {
            api.body = [];
          } else {
            api.body = this.body.formData;
          }
        } else if (api.body_type === 'url_encoded') {
          if (!this?.body?.urlencoded) {
            api.body = [];
          } else {
            api.body = this.convertArrayToObject(this.body.urlencoded);
          }
        } else if (api.body_type === 'raw') {
          if (!this?.body?.raw) {
            api.body = '';
          } else {
            api.body = this.body.raw;
          }
          api.bodyLanguage = this.bodyLanguage;
        }
      } else {
        delete api.body;
      }
      return structuredClone({
        id: this.item.id,
        type: this.item.type,
        payload: api,
      });
    },
  },
  created() {
    this.itemChangeHandler();
  },
  methods: {
    ...mapActions(['showToastMessage']),
    // eslint-disable-next-line
    emitPayload: debounce(function () {
      this.$emit('update', this.payload);
    }, 300),
    itemChangeHandler() {
      if (this.item) {
        const apiData = JSON.parse(JSON.stringify(this.item.payload));
        this.headers = this.convertObjectToArray(apiData.headers);
        if (!this.headers.length) this.addHeader();
        // this.parameters = this.convertObjectToArray(apiData.parameters);
        // if (!this.parameters.length) this.addParameter();
        this.responseParameters = this.convertObjectToArray(apiData.response);
        if (!this.responseParameters.length) this.addResponseParameter();
        this.body = {
          formData: [],
          urlencoded: [],
          raw: '',
          binary: '',
        };
        if (!['GET', 'DELETE'].includes(apiData.method)) {
          if (apiData.body_type === 'form_data') {
            this.body.formData = apiData.body;
            if (!this.body.formData.length) this.addBodyItem();
          } else if (apiData.body_type === 'url_encoded') {
            this.body.urlencoded = this.convertObjectToArray(apiData.body);
            if (!this.body.urlencoded.length) this.addBodyItem();
          } else if (apiData.body_type === 'raw') {
            this.body.raw = apiData.body;
          }
        }
        this.api = { ...this.api, ...apiData };
      }
    },
    onBodyTypeChange() {
      if (this.api.body_type === 'form_data' && !this.body.formData.length) {
        this.addBodyItem();
      } else if (this.api.body_type === 'url_encoded' && !this.body.urlencoded.length) {
        this.addBodyItem();
      }
      this.emitPayload();
    },
    onDeleteHeaderClick(index) {
      this.headers.splice(index, 1);
      this.emitPayload();
    },
    // onDeleteParameterClick(index) {
    //   this.parameters.splice(index, 1);
    // },
    onDeleteBodyClick(index) {
      if (this.api.body_type === 'form_data') this.body.formData.splice(index, 1);
      if (this.api.body_type === 'url_encoded') this.body.urlencoded.splice(index, 1);
      this.emitPayload();
    },
    onDeleteResponseClick(index) {
      this.responseParameters.splice(index, 1);
      this.emitPayload();
    },
    addHeader() {
      this.headers.push({ key: '', value: '' });
    },
    // addParameter() {
    //   this.parameters.push({ key: '', value: '' });
    // },
    addBodyItem() {
      if (this.api.body_type === 'form_data') this.body.formData.push({ key: '', type: 'Text', value: '' });
      if (this.api.body_type === 'url_encoded') this.body.urlencoded.push({ key: '', value: '' });
    },
    addResponseParameter() {
      this.responseParameters.push({ key: '', value: '' });
    },
    convertArrayToObject(arr) {
      return arr
        .filter((header) => header.key)
        .reduce((acc, val) => {
          acc[val.key] = val.value;
          return acc;
        }, {});
    },
    convertObjectToArray(obj) {
      return Object.keys(obj).map((headerKey) => ({ key: headerKey, value: obj[headerKey] }));
    },
  },
  watch: {
    item: {
      handler() {
        this.itemChangeHandler();
      },
      deep: true,
    },
  },
};
</script>
